import {
  type LanguageKeys,
  useTranslation,
} from "@/hooks/useTranslation/useTranslation"
import { Authenticator, useAuthenticator } from "@aws-amplify/ui-react"
import { type ReactNode, useEffect, useState } from "react"

/**
 * `Authenticator.Provider` that is rebuilt when the UI language changed while
 * nobody is signed in.
 *
 * Amplify reads the translated `formFields` (labels, placeholders, the
 * password toggle's screen reader labels) once, when its state machine is
 * first configured, and keeps them through sign-out. After a language switch
 * during the session the sign-in form would otherwise stay in the old
 * language while the rest of the screen switches. Signed in, the provider
 * never remounts: that would tear down the whole app.
 */
export function LanguageAwareAuthenticatorProvider({
  children,
}: {
  children: ReactNode
}) {
  const { currentLanguage } = useTranslation()
  const [formLanguage, setFormLanguage] = useState(currentLanguage)
  return (
    <Authenticator.Provider key={formLanguage}>
      <SignedOutLanguageSync
        formLanguage={formLanguage}
        onStale={setFormLanguage}
      />
      {children}
    </Authenticator.Provider>
  )
}

function SignedOutLanguageSync({
  formLanguage,
  onStale,
}: {
  formLanguage: LanguageKeys
  onStale: (language: LanguageKeys) => void
}) {
  const { currentLanguage } = useTranslation()
  const { authStatus } = useAuthenticator((context) => [context.authStatus])
  useEffect(() => {
    if (authStatus === "unauthenticated" && currentLanguage !== formLanguage) {
      onStale(currentLanguage)
    }
  }, [authStatus, currentLanguage, formLanguage, onStale])
  return null
}
